import React, { useState } from "react";
import {
  Button,
  Card,
  CardBody,
  Col,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from "reactstrap";
import classnames from "classnames";
import { Link } from "react-router-dom";
import StatusBadge from "components/common/StatusBadge";
import { toast } from "react-toastify";
import {
  EmployeeAdvanceStatus,
  employeeAdvanceStatusMap,
} from "features/EmployeeAdvances/constants/employee-advance.constants";
import {
  cancelEmployeeAdvance,
  disburseEmployeeAdvance,
  refundEmployeeAdvance,
} from "features/EmployeeAdvances/services/EmployeeAdvanceService";
import { useEmployeeAdvanceOverview } from "./EmployeeAdvanceOverviewContext";
import OverviewTab from "./OverviewTab";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import CancelModal from "../CancelModal";
import DisburseModal from "../DisburseModal";
import RefundModal from "../RefundModal";

const Section = () => {
  const { can } = useAccess();
  const canEditAdvance = can(Permissions.EMPLOYEE_ADVANCE.UPDATE);
  const canCancelAdvance = can(Permissions.EMPLOYEE_ADVANCE.CANCEL);
  const canDisburseAdvance = can(Permissions.EMPLOYEE_ADVANCE.DISBURSE);
  const canRefundAdvance = can(Permissions.EMPLOYEE_ADVANCE.REFUND);
  const { advance, refetch } = useEmployeeAdvanceOverview();
  const [activeTab, setActiveTab] = useState("1");

  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [disburseOpen, setDisburseOpen] = useState(false);
  const [disburseSubmitting, setDisburseSubmitting] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundSubmitting, setRefundSubmitting] = useState(false);

  if (!advance) {
    return (
      <Row>
        <Col lg={12}>
          <p className="text-muted mb-0">This advance could not be loaded.</p>
        </Col>
      </Row>
    );
  }

  const isDraft = advance.status === EmployeeAdvanceStatus.DRAFT;
  const isActive = advance.status === EmployeeAdvanceStatus.PROCESSED;
  const showEdit = canEditAdvance && isActive;
  const showCancel =
    canCancelAdvance && (isDraft || isActive) && (advance.settledAmount ?? 0) <= 0;
  const showDisburse = canDisburseAdvance && isDraft;
  const showRefund =
    canRefundAdvance && isActive && (advance.settleableAmount ?? 0) > 0;

  const handleDisburse = async (payload: {
    modeOfPaymentId: number;
    paymentDate?: string | null;
    referenceNumber?: string | null;
  }) => {
    setDisburseSubmitting(true);
    try {
      const res = await disburseEmployeeAdvance(advance.id, payload);
      if (res.success) {
        toast.success(res.message ?? "Advance disbursed.");
        setDisburseOpen(false);
        await refetch();
      } else {
        toast.error(res.message ?? "Could not disburse advance.");
      }
    } catch (err) {
      toast.error(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          "Could not disburse advance.",
      );
    } finally {
      setDisburseSubmitting(false);
    }
  };

  const handleRefund = async (payload: {
    modeOfPaymentId: number;
    paymentDate?: string | null;
    referenceNumber?: string | null;
  }) => {
    setRefundSubmitting(true);
    try {
      const res = await refundEmployeeAdvance(advance.id, payload);
      if (res.success) {
        toast.success(res.message ?? "Advance refunded.");
        setRefundOpen(false);
        await refetch();
      } else {
        toast.error(res.message ?? "Could not refund advance.");
      }
    } catch (err) {
      toast.error(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          "Could not refund advance.",
      );
    } finally {
      setRefundSubmitting(false);
    }
  };

  const handleCancel = async () => {
    try {
      const res = await cancelEmployeeAdvance(advance.id, cancelReason);
      if (res.success) {
        toast.success(res.message ?? "Advance cancelled.");
        setCancelOpen(false);
        await refetch();
      } else {
        toast.error(res.message ?? "Could not cancel advance.");
      }
    } catch (err) {
      toast.error(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          "Could not cancel advance.",
      );
    }
  };

  return (
    <React.Fragment>
      <CancelModal
        show={cancelOpen}
        reason={cancelReason}
        onReasonChange={setCancelReason}
        onCancelClick={handleCancel}
        onCloseClick={() => {
          setCancelOpen(false);
          setCancelReason("");
        }}
        record={advance}
      />

      <DisburseModal
        show={disburseOpen}
        record={advance}
        submitting={disburseSubmitting}
        onCloseClick={() => setDisburseOpen(false)}
        onConfirm={(payload) => void handleDisburse(payload)}
      />

      <RefundModal
        show={refundOpen}
        record={advance}
        submitting={refundSubmitting}
        onCloseClick={() => setRefundOpen(false)}
        onConfirm={(payload) => void handleRefund(payload)}
      />

      <Row>
        <Col lg={12}>
          <Card className="mt-n4 mx-n4">
            <div className="bg-light-subtle">
              <CardBody className="pb-0 px-4">
                <Row className="mb-3">
                  <div className="col-md">
                    <div>
                      <h4 className="fw-bold">{advance.code}</h4>
                      <div className="hstack gap-3 flex-wrap">
                        <div className="d-flex align-items-center gap-2">
                          <span className="text-muted">Status:</span>
                          <StatusBadge value={advance.status} map={employeeAdvanceStatusMap} />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-auto">
                    <div className="hstack gap-2">
                      {showDisburse ? (
                        <Button
                          type="button"
                          color="success"
                          size="sm"
                          onClick={() => setDisburseOpen(true)}
                        >
                          <i className="ri-hand-coin-line align-bottom me-1" />
                          Disburse
                        </Button>
                      ) : null}
                      {showRefund ? (
                        <Button
                          type="button"
                          color="danger"
                          size="sm"
                          onClick={() => setRefundOpen(true)}
                        >
                          <i className="ri-arrow-go-back-line align-bottom me-1" />
                          Refund
                        </Button>
                      ) : null}
                      {showCancel ? (
                        <Button
                          type="button"
                          color="danger"
                          size="sm"
                          onClick={() => setCancelOpen(true)}
                        >
                          <i className="ri-close-circle-line align-bottom me-1" />
                          Cancel
                        </Button>
                      ) : null}
                      {showEdit ? (
                        <Link
                          to={`/finance/employee-advances/edit/${advance.id}`}
                          className="btn btn-primary btn-sm"
                        >
                          <i className="ri-pencil-line align-bottom me-1" />
                          Edit
                        </Link>
                      ) : null}
                    </div>
                  </div>
                </Row>

                <Nav className="nav-tabs-custom border-bottom-0" role="tablist">
                  <NavItem>
                    <NavLink
                      className={classnames(
                        { active: activeTab === "1" },
                        "text-body fw-semibold",
                      )}
                      onClick={(e) => {
                        e.preventDefault();
                        setActiveTab("1");
                      }}
                      href="#"
                    >
                      Overview
                    </NavLink>
                  </NavItem>
                </Nav>
              </CardBody>
            </div>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col lg={12} className="text-muted">
          <TabContent activeTab={activeTab}>
            <TabPane tabId="1">
              <OverviewTab />
            </TabPane>
          </TabContent>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default Section;
